"use client";

import { motion, AnimatePresence } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { Linkedin, Mail, Phone } from "lucide-react";
import AzurysLogo from "@/assets/azurys-logo.svg";
import { useLanguage } from "@/contexts/LanguageContext";

const translations = {
  en: {
    description: "Web agency specialized in creating modern and performant websites. Your project delivered in 4 days.",
    copyright: "All rights reserved.",
    footerLinks: [
      {
        title: "Services",
        links: [
          { label: "Website Creation", href: null },
          { label: "Web Redesign", href: null },
          { label: "SEO & Performance", href: null },
          { label: "Maintenance", href: null }
        ]
      },
      {
        title: "Agency",
        links: [
          { label: "Services", href: "#services" },
          { label: "Process", href: "#process" },
          { label: "Offers", href: "#offers" },
          { label: "FAQ", href: "#faq" }
        ]
      },
      {
        title: "Legal",
        links: [
          { label: "Legal Notice", href: "/legal" },
          { label: "Terms & Conditions", href: "/terms" },
          { label: "Privacy Policy", href: "/privacy" }
        ]
      }
    ]
  },
  fr: {
    description: "Agence web spécialisée dans la création de sites internet modernes et performants. Votre projet livré en 4 jours.",
    copyright: "Tous droits réservés.",
    footerLinks: [
      {
        title: "Services",
        links: [
          { label: "Création de site", href: null },
          { label: "Refonte web", href: null },
          { label: "SEO & Performance", href: null },
          { label: "Maintenance", href: null }
        ]
      },
      {
        title: "Agence",
        links: [
          { label: "Services", href: "#services" },
          { label: "Processus", href: "#process" },
          { label: "Offres", href: "#offers" },
          { label: "FAQ", href: "#faq" }
        ]
      },
      {
        title: "Légal",
        links: [
          { label: "Mentions légales", href: "/legal" },
          { label: "CGV", href: "/terms" },
          { label: "Politique de confidentialité", href: "/privacy" }
        ]
      }
    ]
  }
};

const socialLinks = [
  { icon: Linkedin, href: "https://www.linkedin.com/company/110418051", label: "LinkedIn" }
];

export function Footer() {
  const { lang } = useLanguage();
  const t = translations[lang];

  return (
    <footer className="relative border-t border-white/[0.05] bg-card/20">
      <Container>
        <div className="py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
            {/* Brand column */}
            <div className="lg:col-span-2">
              <motion.a href="#hero" className="inline-block mb-4" whileHover={{ scale: 1.02 }}>
                <img src={AzurysLogo} alt="Azurys" className="h-8" />
              </motion.a>
              <AnimatePresence mode="wait">
                <motion.p key={lang + "desc"} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="text-muted-foreground mb-6 max-w-sm">
                  {t.description}
                </motion.p>
              </AnimatePresence>
              
              {/* Contact info */}
              <div className="space-y-3 mb-6">
                <a href="mailto:azurysdesign@gmail.com" className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors">
                  <Mail size={16} className="text-primary" />
                  azurysdesign@gmail.com
                </a>
                <a href="tel:+33672107542" className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors">
                  <Phone size={16} className="text-primary" />
                  +33 6 72 10 75 42    
                </a>
              </div>

              {/* Social links */}
              <div className="flex items-center gap-3">
                {socialLinks.map(social => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-10 h-10 rounded-lg bg-card border border-white/[0.08] flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-white/[0.15] transition-colors"
                    aria-label={social.label}
                  >
                    <social.icon size={18} />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Links columns */}
            {t.footerLinks.map(column => (
              <div key={column.title}>
                <ul className="space-y-3">
                  {column.links.map(link => (
                    <li key={link.label}>
                      {link.href ? (
                        <a href={link.href} className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm">
                          {link.label}
                        </a>
                      ) : (
                        <span className="text-muted-foreground text-sm">
                          {link.label}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="py-6 border-t border-white/[0.05] flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Azurys. {t.copyright}
          </p>
        </div>
      </Container>
    </footer>
  );
}
